/**
 * Brand colours for the values these screens repeat constantly.
 *
 * All drawn from the palette in `tailwind.config.js` — primary (green),
 * secondary (coral), creams, action and neutral. Nothing invented: a one-off
 * hex here is a colour nobody else can reuse and that no design change reaches.
 *
 * The rule behind the choices: colour carries meaning or it is noise. A
 * vertical is a category, so the three get distinct hues. A status is a
 * position in a funnel, so it runs cool to warm. Anything needing attention
 * borrows the alert and danger colours already used elsewhere in the admin.
 */
export const CHIP =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-gilroy-medium';

/**
 * Three categories, three hues. Not a ranking — no colour is "better".
 *
 * None of them is coral. Red is reserved for a lost outcome, and a furnishing
 * lead is not a bad thing: a vertical wearing the loss colour makes a healthy
 * queue look alarming.
 */
export const VERTICAL_STYLE: Record<string, string> = {
  furnishing: 'bg-blue text-neutral-500',
  acquisition: 'bg-primary-50 text-primary-700',
  reservation: 'bg-creams-500 text-creams-900'
};

/**
 * Cool while the lead is live, green when it lands, warm when it stalls.
 * `duplicate` is deliberately flat — it is bookkeeping, not an outcome.
 */
export const STATUS_STYLE: Record<string, string> = {
  new: 'bg-blue text-neutral-500',
  pending: 'bg-creams-500 text-creams-900',
  resolved: 'bg-primary-100 text-primary-800',
  // Warm, but not coral — reopening a lead is activity, not a loss.
  reopened: 'bg-creams-600 text-creams-900',
  not_reachable: 'bg-neutral-50 text-neutral-400',
  duplicate: 'bg-neutral-50 text-neutral-300'
};

/** Seniority in the reporting tree, darkest at the top. */
export const ROLE_STYLE: Record<string, string> = {
  manager: 'bg-primary-500 text-neutral-0',
  lead: 'bg-primary-200 text-primary-900',
  senior: 'bg-primary-50 text-primary-700',
  agent: 'bg-creams-400 text-creams-900'
};

export const styleFor = (map: Record<string, string>, key?: string): string =>
  (key && map[key]) || 'bg-neutral-50 text-neutral-400';

/**
 * Colour for how a lead ended.
 *
 * Driven by the service's own classification — `wonResolutionTypes` and
 * `systemOnlyResolutionTypes` come from `GET /leads/config`. A local list of
 * "good" outcomes would be a second opinion about the funnel that drifts the
 * moment a resolution is added, and the won set genuinely differs per vertical.
 *
 *  - won        -> green. quote accepted, unit onboarded, booked.
 *  - system-set -> neutral. `unreachable` and `stale_auto_closed` are stamped
 *                  by the service when a quota or a timer runs out. Nobody
 *                  judged the lead, so nothing should look judged.
 *  - everything else -> red. not interested, not qualified, no availability.
 */
export function resolutionStyle(
  resolutionType?: string,
  wonTypes: string[] = [],
  systemTypes: string[] = []
): string {
  if (!resolutionType) return 'bg-neutral-50 text-neutral-400';
  if (wonTypes.includes(resolutionType)) return 'bg-primary-100 text-primary-800';
  if (systemTypes.includes(resolutionType)) return 'bg-neutral-50 text-neutral-400';
  return 'bg-secondary-100 text-secondary-800';
}
